import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import budgetService from '../services/budgetService';

// 29 ago 2026 (pedido de Augusto: "vamos a crear el presupuesto"): este
// slice estaba escrito contra un contrato que nunca existió en ningún
// backend -- `amount`/`spent`/`description` en vez de los campos reales
// del backend (categoria/monto/periodo, ver backend_new/app/schemas/
// budget.py), y `calculateSummary` sumaba `b.amount`/`b.spent` que
// siempre eran `undefined` (NaN en la práctica). Se reescribe contra el
// contrato real: `list` son los Budget crudos (para editar/borrar) y
// `summary` es la respuesta de GET /budgets/summary -- un array por
// categoría con `{categoria, presupuesto, gastado}`, ya calculado por el
// backend sobre el mes en curso y solo en soles (ver
// app/services/budget_alerts.py::gastado_mes_categoria). El frontend no
// vuelve a sumar nada por su cuenta: solo muestra lo que el backend ya
// calculó.
export const fetchBudgets = createAsyncThunk(
  'budgets/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await budgetService.getAll();
      return response.data;
    } catch (error) {
      // 21 oct 2026 (Augusto: "no se actualiza el presupuesto con las
      // nuevas medidas dadas"). Sin este respaldo, un fallo sin respuesta
      // --red caída, CORS, timeout-- dejaba el error en undefined, la
      // alerta de la pantalla no se dibujaba, y el guardado fallaba en
      // absoluto silencio: el usuario ve el monto viejo y no sabe por qué.
      return rejectWithValue(
        error.response?.data || { detail: error.message || 'No se pudo guardar. Revisa tu conexión e inténtalo de nuevo.' }
      );
    }
  }
);

export const fetchBudgetSummary = createAsyncThunk(
  'budgets/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await budgetService.getSummary();
      return response.data;
    } catch (error) {
      // 21 oct 2026 (Augusto: "no se actualiza el presupuesto con las
      // nuevas medidas dadas"). Sin este respaldo, un fallo sin respuesta
      // --red caída, CORS, timeout-- dejaba el error en undefined, la
      // alerta de la pantalla no se dibujaba, y el guardado fallaba en
      // absoluto silencio: el usuario ve el monto viejo y no sabe por qué.
      return rejectWithValue(
        error.response?.data || { detail: error.message || 'No se pudo guardar. Revisa tu conexión e inténtalo de nuevo.' }
      );
    }
  }
);

export const addBudget = createAsyncThunk(
  'budgets/add',
  async (budgetData, { dispatch, rejectWithValue }) => {
    try {
      const response = await budgetService.create(budgetData);
      dispatch(fetchBudgetSummary());
      return response.data;
    } catch (error) {
      // 21 oct 2026 (Augusto: "no se actualiza el presupuesto con las
      // nuevas medidas dadas"). Sin este respaldo, un fallo sin respuesta
      // --red caída, CORS, timeout-- dejaba el error en undefined, la
      // alerta de la pantalla no se dibujaba, y el guardado fallaba en
      // absoluto silencio: el usuario ve el monto viejo y no sabe por qué.
      return rejectWithValue(
        error.response?.data || { detail: error.message || 'No se pudo guardar. Revisa tu conexión e inténtalo de nuevo.' }
      );
    }
  }
);

export const updateBudget = createAsyncThunk(
  'budgets/update',
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await budgetService.update(id, data);
      dispatch(fetchBudgetSummary());
      return response.data;
    } catch (error) {
      // 21 oct 2026 (Augusto: "no se actualiza el presupuesto con las
      // nuevas medidas dadas"). Sin este respaldo, un fallo sin respuesta
      // --red caída, CORS, timeout-- dejaba el error en undefined, la
      // alerta de la pantalla no se dibujaba, y el guardado fallaba en
      // absoluto silencio: el usuario ve el monto viejo y no sabe por qué.
      return rejectWithValue(
        error.response?.data || { detail: error.message || 'No se pudo guardar. Revisa tu conexión e inténtalo de nuevo.' }
      );
    }
  }
);

export const deleteBudget = createAsyncThunk(
  'budgets/delete',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await budgetService.delete(id);
      dispatch(fetchBudgetSummary());
      return id;
    } catch (error) {
      // 21 oct 2026 (Augusto: "no se actualiza el presupuesto con las
      // nuevas medidas dadas"). Sin este respaldo, un fallo sin respuesta
      // --red caída, CORS, timeout-- dejaba el error en undefined, la
      // alerta de la pantalla no se dibujaba, y el guardado fallaba en
      // absoluto silencio: el usuario ve el monto viejo y no sabe por qué.
      return rejectWithValue(
        error.response?.data || { detail: error.message || 'No se pudo guardar. Revisa tu conexión e inténtalo de nuevo.' }
      );
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  error: null,
  // Array de { categoria, presupuesto, gastado } -- ver GET /budgets/summary.
  summary: [],
  summaryLoading: false,
};

const budgetSlice = createSlice({
  name: 'budgets',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBudgets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBudgets.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchBudgets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchBudgetSummary.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(fetchBudgetSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summary = action.payload;
      })
      .addCase(fetchBudgetSummary.rejected, (state) => {
        state.summaryLoading = false;
      });

    builder
      .addCase(addBudget.fulfilled, (state, action) => {
        state.list.push(action.payload);
      })
      .addCase(addBudget.rejected, (state, action) => {
        state.error = action.payload;
      });

    builder
      .addCase(updateBudget.fulfilled, (state, action) => {
        const index = state.list.findIndex((b) => b.id === action.payload.id);
        if (index !== -1) state.list[index] = action.payload;
      })
      .addCase(updateBudget.rejected, (state, action) => {
        state.error = action.payload;
      });

    builder
      .addCase(deleteBudget.fulfilled, (state, action) => {
        state.list = state.list.filter((b) => b.id !== action.payload);
      })
      .addCase(deleteBudget.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError } = budgetSlice.actions;
export default budgetSlice.reducer;
